import { type ReactNode, createContext, useContext } from 'react'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  Flex,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'

// chakra v3.29 + React 18 の型定義で Accordion 系の style props / value が欠落するため
// cast する (`~/components/common/CsvUpload/ColumnGuideTable.tsx` と同じ扱い)。
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

/** 折りたたみ式のときの AccordionItem の value (1 項目だけなので固定)。 */
const COLLAPSIBLE_ITEM = 'filter'

/**
 * フィルタラベルの書体系統。design に 2 系統
 * (`<label>` / `.filter-label` = 700 / `.form-label` = 600) が実在するため
 * 行単位で選ぶ (FilterSection 単位では選べない、行単位で 1 回だけ選ぶ)。
 * `~/constants/typography.ts` の canonical textStyle (`filterLabel` = md/bold,
 * `label` = md/semibold) を消費し、fontSize/fontWeight を手書きしない。
 */
type LabelVariant = 'filterLabel' | 'formLabel'

const LABEL_VARIANT_TEXT_STYLE: Record<LabelVariant, 'filterLabel' | 'label'> =
  {
    filterLabel: 'filterLabel',
    formLabel: 'label',
  }

// 既定 'filterLabel' (=700) を全 FilterSection へ Context で配布する。
const LabelVariantContext = createContext<LabelVariant>('filterLabel')

type FilterToolbarProps = {
  children: ReactNode
  /**
   * フィルタ行 (= 直下の FilterSection 群) のラベル書体。既定 'filterLabel' (=700)。
   * 行単位で 1 回だけ選び、Context で全 FilterSection へ配布する。
   */
  labelVariant?: LabelVariant
  /** 右端 (marginLeft:auto 内部固定) のリセット等操作スロット。 */
  resetAction?: ReactNode
  /**
   * フィルタ行 (wrap 対象の Flex) の下に表示する補足情報 (例: 代表値の説明ノート)。
   * wrap 対象の Flex には含めず、カード内の別行として表示する (= フィルタ項目の折返しに巻き込まれない)。
   * 省略時は何も描画しない (既存呼び出しに影響なし)。
   */
  footer?: ReactNode
  /**
   * 折りたたんでも見せる要約 (例: 選択中の条件のチップ)。渡すとフィルタ行を DS Accordion に
   * 収め、要約の行のクリックで開閉する。**既定は展開** (開閉の状態は画面を離れると戻る)。
   * 省略時は従来どおり常に展開したフィルタ行 (既存呼び出しに影響なし)。
   */
  summary?: ReactNode
}

/**
 * フィルタ行の骨格 (frontend-design-unification 設計書 §5.1)。
 * gap '16px 20px' (design `.ecl-toolbar` 正本値) / padding 12px / border 1px
 * Border.lowEmphasis / background Surface.primary を内部固定し、外側 margin は
 * 0 固定 (縦 spacing の所有権は PageBody の gap に一本化)。スタイル値を props に
 * 出さない (docs/adr/frontend-skeleton-closed-style-props.md)。
 */
export const FilterToolbar = ({
  children,
  labelVariant = 'filterLabel',
  resetAction,
  footer,
  summary,
}: FilterToolbarProps): React.ReactElement => {
  const body = (
    <>
      <LabelVariantContext.Provider value={labelVariant}>
        <Flex alignItems="center" flexWrap="wrap" gap="16px 20px">
          {children}
          {resetAction !== undefined && (
            <Box marginLeft="auto">{resetAction}</Box>
          )}
        </Flex>
      </LabelVariantContext.Provider>
      {footer}
    </>
  )

  if (summary === undefined) {
    return (
      <Box
        padding={3}
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        {body}
      </Box>
    )
  }

  /*
   * 折りたたみ式 (design `plan/submission-detail.html` の `.sd-filter-accordion`)。
   * 外枠は常時展開と同じ (border / radius / 背景)。要約の行は design `.sd-filter-summary`
   * (padding 12px 16px / 高さ 52px 以上)、開閉標は左端 (`.ecl-accordion-trigger::before`)、
   * hover は `.ecl-accordion-trigger:hover`。フィルタ行は区切り線の下に常時展開と同じ padding で置く。
   * 本文の overflow は Chakra の accordion recipe が hidden にするが、エリアの Select と
   * 日付のカレンダーは body へ portal しているので切れない。
   */
  return (
    <AccordionRoot collapsible defaultValue={[COLLAPSIBLE_ITEM]}>
      <AccordionItem
        overflow="hidden"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor="Surface.primary"
        value={COLLAPSIBLE_ITEM}
      >
        <AccordionItemTrigger
          minHeight="52px"
          _hover={{ backgroundColor: 'Hover.lowEmphasis' }}
          cursor="pointer"
          paddingX={4}
          paddingY={3}
        >
          <AccordionDisclosureIcon />
          {summary}
        </AccordionItemTrigger>
        <AccordionItemContent
          padding={3}
          borderTopWidth="1px"
          borderTopColor="Border.lowEmphasis"
        >
          {body}
        </AccordionItemContent>
      </AccordionItem>
    </AccordionRoot>
  )
}

type FilterSectionProps = {
  label: string
  children: ReactNode
  /** 残り幅を埋めるフィールドか (数値 flex は廃止)。省略時は通常のフレックス項目として振る舞う。 */
  grow?: boolean
}

export const FilterSection = ({
  label,
  children,
  grow = false,
}: FilterSectionProps): React.ReactElement => {
  const labelVariant = useContext(LabelVariantContext)

  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      flex={grow ? 1 : undefined}
      gap={2}
      // UI 辞書 components/toolbar.md `.ecl-toolbar > .filter-group { min-height: 40px }`:
      // 中の DS コントロール (40px) に合わせ、 折り返した時の縦位置を揃える
      minHeight="40px"
    >
      <Text
        textStyle={LABEL_VARIANT_TEXT_STYLE[labelVariant]}
        flexShrink={0}
        color="Text.highEmphasis"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
      {children}
    </Flex>
  )
}
